/**
 * PHASE 5.3: Workspace Home by Slug
 *
 * Fetches workspace home data using slug and renders WorkspaceHome component
 */
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useWorkspaceStore } from '@/state/workspace.store';
import { useAuth } from '@/state/AuthContext';
import { request } from '@/lib/api';

export default function WorkspaceHomeBySlug() {
  const { slug } = useParams<{ slug: string }>();
  const { user, loading: authLoading } = useAuth();
  const { setActiveWorkspace, markWorkspaceHydrated, setHydrating } = useWorkspaceStore();
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !user || !slug) {
      return;
    }

    loadWorkspaceHome();
  }, [authLoading, user, slug]);

  async function loadWorkspaceHome() {
    if (!slug) {
      setError('Missing workspace slug');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setHydrating(true);

    try {
      const resolved = await request.get<{ id?: string }>(`/workspaces/slug/${slug}`);
      const id = resolved?.id;
      if (!id) {
        setError('Workspace not found');
        setLoading(false);
        setHydrating(false);
        return;
      }
      setActiveWorkspace(id);
      markWorkspaceHydrated(id);
      setWorkspaceId(id);
      setLoading(false);
      setHydrating(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 404 ? 'Workspace not found' : 'This workspace could not be opened');
      setLoading(false);
      setHydrating(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="workspace-slug-home-unavailable">
        <p className="text-sm text-slate-700">{error}</p>
      </div>
    );
  }

  if (workspaceId) {
    return <Navigate to={`/workspaces/${workspaceId}/home`} replace />;
  }

  return null;
}
